import { useMemo } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useMarketingStore } from '../../store/marketing.store';
import { Download } from 'lucide-react';
import InfoTooltip from '../revenue/InfoTooltip';

function formatVND(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

export default function EfficiencyTrendChart({ isDark }: { isDark: boolean }) {
  const efficiencyTrend = useMarketingStore((s) => s.efficiencyTrend);
  const reportFrom = useMarketingStore((s) => s.reportFrom);
  const reportTo = useMarketingStore((s) => s.reportTo);

  const tooltipText = "Theo dõi hiệu quả chi phí quảng cáo hàng ngày.\n\n• Đường xanh: CPL = spend ÷ actions[onsite_conversion.lead]\n• Đường cam: CPC = spend ÷ clicks (Meta API field: cpc)\n• Đường tím: CTR = clicks ÷ impressions × 100\n\nNguồn: Facebook Ads Insights API, time_increment=1 (daily)";

  const averages = useMemo(() => {
    if (!efficiencyTrend.length) return { cpl: 0, cpc: 0, ctr: 0 };
    const cplDays = efficiencyTrend.filter(d => d.cpl > 0);
    const cpcDays = efficiencyTrend.filter(d => d.cpc > 0);
    const ctrDays = efficiencyTrend.filter(d => d.ctr > 0);
    return {
      cpl: cplDays.length > 0 ? cplDays.reduce((s, d) => s + d.cpl, 0) / cplDays.length : 0,
      cpc: cpcDays.length > 0 ? cpcDays.reduce((s, d) => s + d.cpc, 0) / cpcDays.length : 0,
      ctr: ctrDays.length > 0 ? ctrDays.reduce((s, d) => s + d.ctr, 0) / ctrDays.length : 0,
    };
  }, [efficiencyTrend]);

  const exportCSV = () => {
    const csvRows = ['Ngày,CPL,CPC,CTR,CPM,Chi phí/Tin nhắn,Chi phí,Leads,Tin nhắn'];
    for (const row of efficiencyTrend) {
      csvRows.push(`"${row.date}",${row.cpl},${row.cpc},${row.ctr},${row.cpm},${row.costPerMessage},${row.spend},${row.leads},${row.messages}`);
    }
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `efficiency_trend_${reportFrom}_${reportTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!efficiencyTrend.length) {
    return (
      <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Hiệu quả chi phí theo thời gian
          <InfoTooltip isDark={isDark} text={tooltipText} />
        </h3>
        <div className={`flex items-center justify-center h-64 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          Chưa có dữ liệu
        </div>
      </div>
    );
  }

  const cards = [
    { label: 'CPL trung bình (Facebook)', value: formatVND(averages.cpl) + ' VND', color: 'text-emerald-400', tooltip: 'Cost Per Lead (Facebook) — Chi phí trung bình để có 1 lead từ Facebook Instant Form.\n\nLưu ý: Đây là Facebook leads (form đăng ký), KHÁC với HubSpot leads (lifecycle stage).\n\nCông thức: spend ÷ actions[onsite_conversion.lead]\nNguồn: Facebook Ads API' },
    { label: 'CPC trung bình', value: formatVND(averages.cpc) + ' VND', color: 'text-amber-400', tooltip: 'Cost Per Click — Chi phí trung bình cho 1 lượt click.\n\nCông thức: spend ÷ clicks\n\nMeta API field: cpc' },
    { label: 'CTR trung bình', value: averages.ctr.toFixed(2) + '%', color: 'text-purple-400', tooltip: 'Click-Through Rate — Tỷ lệ click trên tổng lượt hiển thị.\n\nCông thức: clicks ÷ impressions × 100\n\nMeta API: tính từ clicks và impressions' },
  ];

  return (
    <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Hiệu quả chi phí theo thời gian
          <InfoTooltip isDark={isDark} text={tooltipText} />
          {' '}
          <span className={`ml-2 text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            ({reportFrom} — {reportTo})
          </span>
        </h3>
        <div className="flex-1" />
        <button onClick={exportCSV} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`}>
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      {/* Scorecards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {c.label}
              <InfoTooltip isDark={isDark} text={c.tooltip} />
            </p>
            <p className={`text-lg font-bold font-mono mt-1 ${isDark ? c.color : 'text-gray-900'}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={efficiencyTrend} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
          <XAxis
            dataKey="date"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
            interval={efficiencyTrend.length > 14 ? Math.ceil(efficiencyTrend.length / 14) : 0}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
            tickFormatter={(v) => formatVND(v)}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: isDark ? '#f8fafc' : '#111827',
            }}
            formatter={(value: any, name: any) => {
              const labels: Record<string, string> = {
                cpl: 'CPL (Facebook)',
                cpc: 'CPC',
                ctr: 'CTR',
              };
              if (name === 'ctr') return [`${(value as number).toFixed(2)}%`, labels[name] || name];
              return [formatVND(value as number) + ' VND', labels[name] || name];
            }}
            labelStyle={{ color: isDark ? '#f8fafc' : '#111827', fontWeight: 600 }}
          />
          <Line yAxisId="left" type="monotone" dataKey="cpl" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 3 }} name="cpl" />
          <Line yAxisId="left" type="monotone" dataKey="cpc" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} name="cpc" />
          <Line yAxisId="right" type="monotone" dataKey="ctr" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', r: 3 }} name="ctr" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
